import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  ReactNode,
} from "react";
import { BalanceConfirmationHandoffProvider } from "./BalanceConfirmationHandoffContext";

/** Local types for layout notifications/pending actions (no longer in cds_client) */
export interface Notification {
  id?: string;
  [key: string]: unknown;
}
export interface PendingActions {
  id?: string;
  [key: string]: unknown;
}

interface BusinessUnit {
  gst_in: string;
  gst_session_expires_at: string;
  gst_session_validity: string;
  gst_username: string;
}

// Chat config: recon-playground only (streaming; conversations, messages, invoke).
export interface InitializeChatOptions {
  onProgress?: (messages: any[]) => void;
}
interface ChatConfiguration {
  onSendMessage?: (message: string) => Promise<string> | Promise<void> | void;
  isLoading?: boolean;
  loadingMessage?: string;
  externalData?: string;
  onInitializeChat?: (options?: InitializeChatOptions) => Promise<any[]>;
}

interface LayoutContextType {
  userName: string;
  customerName: string;
  showRightBar: boolean;
  pendingActionsData: PendingActions[];
  notificationData: Notification[];
  isOverlayOpen: boolean;
  businessUnits: BusinessUnit[];
  isCollapsed: boolean;
  chatInterface: boolean;
  chatbot: boolean;
  showNavbar: boolean;
  showReconDashboard: boolean;
  chatConfig: ChatConfiguration;
  /** Call to request Layout to re-run chat init (e.g. when job metadata loads). Set by Layout. */
  chatInitTriggerRef: React.MutableRefObject<(() => void) | undefined>;
  setUserName: (name: string) => void;
  setCustomerName: (name: string) => void;
  setShowRightBar: (show: boolean) => void;
  setPendingActionsData: (data: PendingActions[]) => void;
  setNotificationData: (data: Notification[]) => void;
  setIsOverlayOpen: (isOpen: boolean) => void;
  setBusinessUnits: (units: BusinessUnit[]) => void;
  setIsCollapsed: (isCollapsed: boolean) => void;
  setChatInterface: (isOpen: boolean) => void;
  setChatbot: (isOpen: boolean) => void;
  setShowNavbar: (isOpen: boolean) => void;
  setShowReconDashboard: (show: boolean) => void;
  setChatConfig: (config: ChatConfiguration) => void; // Add setter for chat config
  resetLayout: () => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return context;
};

interface LayoutProviderProps {
  children: ReactNode;
}

// Define initial state values
const initialState = {
  userName: "",
  customerName: "",
  showRightBar: false,
  pendingActionsData: [] as PendingActions[],
  notificationData: [] as Notification[],
  isOverlayOpen: false,
  businessUnits: [] as BusinessUnit[],
  isCollapsed: true, // Default to collapsed - pages can expand if needed
  chatInterface: false,
  chatbot: true,
  showNavbar: true,
  showReconDashboard: false,
  chatConfig: {} as ChatConfiguration, // Add initial chat config
};

export const LayoutProvider: React.FC<LayoutProviderProps> = ({ children }) => {
  const [userName, setUserName] = useState<string>(initialState.userName);
  const [customerName, setCustomerName] = useState<string>(
    initialState.customerName,
  );
  const [showRightBar, setShowRightBar] = useState<boolean>(
    initialState.showRightBar,
  );
  const [pendingActionsData, setPendingActionsData] = useState<
    PendingActions[]
  >(initialState.pendingActionsData);
  const [notificationData, setNotificationData] = useState<Notification[]>(
    initialState.notificationData,
  );
  const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(
    initialState.isOverlayOpen,
  );
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>(
    initialState.businessUnits,
  );
  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    initialState.isCollapsed,
  );
  const [chatInterface, setChatInterface] = useState<boolean>(
    initialState.chatInterface,
  );
  const [chatbot, setChatbot] = useState<boolean>(initialState.chatbot);
  const [showNavbar, setShowNavbar] = useState<boolean>(
    initialState.showNavbar,
  );
  const [showReconDashboard, setShowReconDashboard] = useState<boolean>(
    initialState.showReconDashboard,
  );
  const [chatConfig, setChatConfig] = useState<ChatConfiguration>(
    initialState.chatConfig,
  );
  const chatInitTriggerRef = useRef<(() => void) | undefined>(undefined);

  const resetLayout = useCallback(() => {
    setUserName(initialState.userName);
    setCustomerName(initialState.customerName);
    setShowRightBar(initialState.showRightBar);
    setPendingActionsData(initialState.pendingActionsData);
    setNotificationData(initialState.notificationData);
    setIsOverlayOpen(initialState.isOverlayOpen);
    setBusinessUnits(initialState.businessUnits);
    setIsCollapsed(initialState.isCollapsed);
    setChatInterface(initialState.chatInterface);
    setChatbot(initialState.chatbot);
    setShowNavbar(initialState.showNavbar);
    setShowReconDashboard(initialState.showReconDashboard);
  }, []);

  const value: LayoutContextType = {
    userName,
    customerName,
    showRightBar,
    pendingActionsData,
    notificationData,
    isOverlayOpen,
    businessUnits,
    isCollapsed,
    chatInterface,
    chatbot,
    showNavbar,
    showReconDashboard,
    chatConfig,
    chatInitTriggerRef,
    setUserName,
    setCustomerName,
    setShowRightBar,
    setPendingActionsData,
    setNotificationData,
    setIsOverlayOpen,
    setBusinessUnits,
    setIsCollapsed,
    setChatInterface,
    setChatbot,
    setShowNavbar,
    setShowReconDashboard,
    setChatConfig, // Add setter to context value
    resetLayout,
  };

  return (
    <BalanceConfirmationHandoffProvider>
      <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
    </BalanceConfirmationHandoffProvider>
  );
};
